<template>
    <div>
      <!-- Card moderno para a tabela -->
      <div class="bg-white rounded-xl shadow-lg overflow-hidden">
        <!-- Header do card -->
        <div class="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 class="text-xl font-bold text-gray-900">👥 Usuários</h2>
              <p class="text-sm text-gray-500 mt-1">Gerenciar usuários do sistema</p>
            </div>
            <!-- Campo de busca -->
            <div class="w-full sm:w-64">
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fas fa-search text-gray-400"></i>
                </div>
                <input 
                  v-model="searchUser" 
                  type="text" 
                  class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  placeholder="Buscar usuário...">
              </div>
            </div>
          </div>
        </div>

        <LoadingComponent v-if="this.loading" />

        <!-- Tabela Desktop -->
        <div class="hidden sm:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissões
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(user) in filteredUsers" :key="user._id" class="hover:bg-gray-50 transition-colors duration-150">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10">
                      <div class="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <i class="fas fa-user text-gray-500"></i>
                      </div>
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">
                        {{ user.name }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-600">{{ user.email }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="flex flex-wrap gap-1">
                    <span 
                      v-for="(rule, id) in user.rules" 
                      :key="id"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {{ rule }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex items-center justify-end gap-2">
                    <router-link 
                      :to="{ name: 'UserForm', params: {userId: user._id}}" 
                      class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-lg text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-150">
                      <i class="fas fa-edit mr-1"></i>
                      Editar
                    </router-link>
                    <button 
                      :disabled="deleteDisable" 
                      @click="deleteUser(user._id)" 
                      class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-150 disabled:opacity-50">
                      <i class="fas fa-trash mr-1"></i>
                      Excluir
                    </button>
                    <router-link 
                      v-bind:class="{'hidden': !hasPermission('SuperAdmin')}"
                      :to="{ name: 'NewBetting', params: {userId: user._id}}" 
                      class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150">
                      <i class="fas fa-plus mr-1"></i>
                      Palpite
                    </router-link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Cards Mobile -->
        <div class="block sm:hidden p-4 space-y-4">
          <div v-for="(user, index) in filteredUsers" :key="index" class="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors duration-150">
            <div class="flex items-start gap-3 mb-3">
              <div class="flex-shrink-0">
                <div class="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                  <i class="fas fa-user text-gray-500 text-lg"></i>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-base font-semibold text-gray-900 truncate">{{ user.name }}</p>
                <p class="text-sm text-gray-600 truncate">{{ user.email }}</p>
                <div class="flex flex-wrap gap-1 mt-2">
                  <span 
                    v-for="(rule, id) in user.rules" 
                    :key="id"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {{ rule }}
                  </span>
                </div>
              </div>
            </div>
            
            <!-- Botões de ação mobile -->
            <div class="grid grid-cols-2 gap-2">
              <router-link 
                :to="{ name: 'UserForm', params: {userId: user._id}}" 
                class="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg text-white bg-gray-600 hover:bg-gray-700 transition-colors duration-150">
                <i class="fas fa-edit"></i>
                <span>Editar</span>
              </router-link>
              <button 
                :disabled="deleteDisable" 
                @click="deleteUser(user._id, index)" 
                class="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors duration-150">
                <i class="fas fa-trash"></i>
                <span>Excluir</span>
              </button>
              <router-link 
                v-bind:class="{'hidden': !hasPermission('SuperAdmin')}"
                :to="{ name: 'NewBetting', params: {userId: user._id}}" 
                class="col-span-2 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors duration-150">
                <i class="fas fa-plus"></i>
                <span>Criar Palpite</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import UserService from '@/services/userService';
import LoadingComponent from './LoadingComponent.vue';
import jwt_decode from 'jwt-decode';
import { mapGetters } from 'vuex';
import { useToast } from "vue-toastification";

export default {
  name: 'UserTable',
  components: {
    LoadingComponent,
  },
  setup() {
      const toast = useToast();
      return { toast }
  },
  created() {
    let token = localStorage.getItem('token');
    if (token !== null) {
     try {
        let payload = jwt_decode(token);
        this.userData = payload;
      } catch (err) { console.log(err)}
    }
  },
  computed: {
    filteredUsers() {
      let users = [];
      users = this.users.filter((user) => {
        return (
          user.name.toLowerCase().indexOf(this.searchUser.toLowerCase()) > -1
        );
      });

      return users;
    },
    ...mapGetters('user', {
      userProfile: 'userProfile',
    })
  },
  mounted() {
    UserService.getUsers().then(data => {
      this.users = data;
      this.loading = false;
    }).catch(err => {
      console.log(err);
      this.loading = false;
      this.toast.error("Não foi possível carregar os usuários.");
    })
  },
  methods: {
    hasPermission(permission) {
      if (this.userData.length == 0) {
        return false;
      }
      let { rules } = this.userData;
      return rules.filter(data => data === permission).length > 0;
    },
    deleteUser(userId) {
      this.deleteDisable = true;
      if (confirm("Deseja deletar o usuario?")) {
        UserService.deleteUser(userId).then(() => {
          this.toast.success("Usuario deletado com sucesso", {
            onClose: () => {
              location.reload();
            }
          });
        }).catch(err => {
          console.log(err);
          this.deleteDisable = false;
          this.toast.error("Não foi possível deletar o usuário.");
        })
        return;
      }
      this.deleteDisable = false;
    },
     removeRow(index, message) {
      if (message !== undefined) {
        this.toast.success(message);
      }
      this.users.splice(index, 1);
    },
  },
  data(){
    return {
      searchUser: "",
      users: [],
      deleteDisable: false,
      loading: true,
    }
  },
}
</script>